import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  FileUploadOutlined as FileUploadOutlinedIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmptyState from '../common/EmptyState';
import PageContainer from '../common/PageContainer';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ListSurface = styled(AdminPanelCard)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableHead-root .MuiTableRow-root': {
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
    color: '#64748b',
    fontSize: '0.78rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    backgroundColor: '#f5f7fb',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: '#f8fbff',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
    borderBottom: 'none',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
    borderLeft: 'none',
    borderRight: 'none',
    fontSize: '0.86rem',
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(0.9),
    paddingBottom: theme.spacing(0.9),
  },
}));

const StudentList = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const schoolId = localStorage.getItem('schoolId');

  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const anyBusy = loading || uploading || downloadingTemplate || exporting;
  const totalStudents = students.length;

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/students', {
        params: {
          classId: selectedClass || undefined,
          sectionId: selectedSection || undefined,
          search: searchTerm || undefined,
          pageNo: currentPage,
          pageSize,
        },
        skipGlobalError: true,
      });
      const responseData = response?.data || {};
      const list = Array.isArray(responseData?.data) ? responseData.data : [];
      const totalRecords = Number(responseData?.totalRecords || 0);
      setStudents(list);
      setTotalPages(Math.max(1, Math.ceil(totalRecords / pageSize)));
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to fetch students.'), 'error');
      setStudents([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, selectedClass, selectedSection, showNotification]);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/classes', { skipGlobalError: true });
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to fetch classes.'), 'error');
      setClasses([]);
    }
  }, [showNotification]);

  const fetchSections = useCallback(
    async (classId) => {
      if (!classId) {
        setSections([]);
        return;
      }
      try {
        const response = await axiosInstance.get(`/api/sections/class/${classId}`, { skipGlobalError: true });
        setSections(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        showNotification(getApiErrorMessage(error, 'Failed to fetch sections.'), 'error');
        setSections([]);
      }
    },
    [showNotification]
  );

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
    }
  }, [selectedClass, fetchSections]);

  const handleStudentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const isValidType = allowedTypes.includes(file.type) || /\.(xlsx|xls)$/i.test(file.name);
    if (!isValidType) {
      showNotification('Please upload a valid Excel file (.xlsx or .xls).', 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const EXPECTED_HEADERS = [
      'First Name',
      'Last Name',
      'Gender',
      'Date of Birth (yyyy-MM-dd)',
      'Phone',
      'Email',
      'Class Name',
      'Section Name',
    ];
    const ALLOWED_HEADER_SYNONYMS = [
      ['first name', 'firstname', 'first'],
      ['last name', 'lastname', 'last'],
      ['gender', 'sex'],
      ['date of birth (yyyy-mm-dd)', 'date of birth', 'dob'],
      ['phone', 'phone number', 'mobile', 'mobile number'],
      ['email', 'email id', 'email address'],
      ['class name', 'class'],
      ['section name', 'section'],
    ];

    const normalizeHeader = (value) =>
      String(value ?? '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    const isRowEmpty = (row) => !row || row.every((cell) => String(cell ?? '').trim() === '');

    const validateStudentSheet = async () => {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) {
        return 'Uploaded file does not contain any sheets.';
      }
      if (firstSheetName !== 'Students') {
        return 'First sheet must be named "Students". Please move Students sheet to the first position.';
      }
      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      if (!rows?.length) {
        return 'Students sheet is empty.';
      }
      const headerRow = rows[0];
      const normalizedHeader = headerRow.map(normalizeHeader);
      const expectedNormalized = EXPECTED_HEADERS.map(normalizeHeader);
      const headerMatches = expectedNormalized.every((_, idx) =>
        ALLOWED_HEADER_SYNONYMS[idx].includes(normalizedHeader[idx])
      );
      if (!headerMatches) {
        return `Header mismatch. Expected: ${EXPECTED_HEADERS.join(', ')}`;
      }
      for (let i = 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (isRowEmpty(row)) continue;
        const excelRow = i + 1;
        const firstName = String(row[0] ?? '').trim();
        const lastName = String(row[1] ?? '').trim();
        const gender = String(row[2] ?? '').trim();
        const dob = String(row[3] ?? '').trim();
        const className = String(row[6] ?? '').trim();
        const sectionName = String(row[7] ?? '').trim();
        if (!firstName) return `Row ${excelRow}: First Name is required.`;
        if (!lastName) return `Row ${excelRow}: Last Name is required.`;
        if (!gender) return `Row ${excelRow}: Gender is required.`;
        if (!dob) return `Row ${excelRow}: Date of Birth is required.`;
        if (!className) return `Row ${excelRow}: Class Name is required.`;
        if (!sectionName) return `Row ${excelRow}: Section Name is required.`;
      }
      return null;
    };

    const validationError = await validateStudentSheet();
    if (validationError) {
      showNotification(validationError, 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (typeof window !== 'undefined') {
      window.__SKHOOL_PAUSE_NOTIFICATIONS__ = true;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post('/api/students/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showNotification(response?.data || 'Students uploaded successfully.', 'success');
      setCurrentPage(1);
      fetchStudents();
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to upload student file.'), 'error');
    } finally {
      setUploading(false);
      if (typeof window !== 'undefined') {
        window.__SKHOOL_PAUSE_NOTIFICATIONS__ = false;
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTemplateDownload = async () => {
    if (!schoolId) {
      showNotification('School ID not found. Please log in again.', 'error');
      return;
    }
    setDownloadingTemplate(true);
    try {
      const response = await axiosInstance.get('/api/students/upload/template', {
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json') || contentType.includes('text/plain')) {
        const message = await response.data.text();
        showNotification(message || 'Could not generate template.', 'info');
        return;
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'StudentUploadTemplate.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showNotification('Student template downloaded.', 'success');
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to download student template.'), 'error');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExportStudents = async () => {
    setExporting(true);
    try {
      const response = await axiosInstance.get('/api/students', {
        params: {
          classId: selectedClass || undefined,
          sectionId: selectedSection || undefined,
          search: searchTerm || undefined,
        },
      });
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];
      if (!list.length) {
        showNotification('No students found to export.', 'info');
        return;
      }

      const rows = [
        [
          'Admission Number',
          'Student Name',
          'Class',
          'Section',
          'Gender',
          'Phone',
          'Email',
          'Status',
        ],
        ...list.map((student) => [
          student?.admissionNumber || '',
          `${student?.firstName || ''} ${student?.lastName || ''}`.trim(),
          student?.classes?.className || '',
          student?.section?.sectionName || '',
          student?.gender || '',
          student?.phone || '',
          student?.email || '',
          student?.status || '',
        ]),
      ];

      const csvContent = rows
        .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `students_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showNotification('Student list exported successfully.', 'success');
    } catch (error) {
      showNotification(getApiErrorMessage(error, 'Failed to export students.'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const pageActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button component="label" variant="outlined" size="small" startIcon={<FileUploadOutlinedIcon />} disabled={anyBusy}>
        Import Students
        <VisuallyHiddenInput
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleStudentUpload}
          ref={fileInputRef}
        />
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={downloadingTemplate ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
        onClick={handleTemplateDownload}
        disabled={anyBusy}
      >
        Template
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
        onClick={handleExportStudents}
        disabled={anyBusy}
      >
        Export List
      </Button>
      <Button component={Link} to="/student/add" variant="contained" size="small" startIcon={<AddIcon />} disabled={anyBusy}>
        Add Student
      </Button>
    </Stack>
  );

  return (
    <PageContainer>
      <AdminPageHero
        title="Students"
        subtitle="Import, export, and manage student records with class and section level filters."
        actions={pageActions}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <ListSurface>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                label="Class"
                onChange={(event) => {
                  setSelectedClass(event.target.value);
                  setSelectedSection('');
                  setCurrentPage(1);
                }}
                disabled={anyBusy}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((classItem) => (
                  <MenuItem key={classItem.classId} value={classItem.classId}>
                    {classItem.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                value={selectedSection}
                label="Section"
                onChange={(event) => {
                  setSelectedSection(event.target.value);
                  setCurrentPage(1);
                }}
                disabled={!selectedClass || anyBusy}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.sectionId} value={section.sectionId}>
                    {section.sectionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <TextField
              size="small"
              fullWidth
              label="Search"
              placeholder="Name or admission number"
              value={searchTerm}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
              disabled={anyBusy}
            />
          </Grid>
        </Grid>

        <SoftTableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Admission No</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : !students.length ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState title="No students found" description="Try changing filters or import students in bulk." />
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student, index) => {
                  const status = String(student?.status || '').toUpperCase();
                  const isActive = status === 'ACTIVE';
                  return (
                    <TableRow hover key={student.studentId}>
                      <TableCell>{(currentPage - 1) * pageSize + index + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Avatar
                            src={student?.imageUrl || undefined}
                            alt={`${student?.firstName || ''} ${student?.lastName || ''}`.trim()}
                            sx={{ width: 34, height: 34 }}
                          />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.15 }}>
                              {`${student?.firstName || ''} ${student?.lastName || ''}`.trim() || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {student?.admissionNumber || 'Admission N/A'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{student?.admissionNumber || '-'}</TableCell>
                      <TableCell>{student?.classes?.className || '-'}</TableCell>
                      <TableCell>{student?.section?.sectionName || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={status || 'N/A'}
                          size="small"
                          color={isActive ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Student">
                          <IconButton size="small" onClick={() => navigate(`/students/${student.studentId}`)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Student">
                          <IconButton size="small" onClick={() => navigate(`/student/edit/${student.studentId}`)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </SoftTableContainer>

        <Box display="flex" justifyContent="center" pt={2} pb={0.5}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
            disabled={anyBusy}
          />
        </Box>
      </ListSurface>
    </PageContainer>
  );
};

export default StudentList;
